import { AlmeAppState } from '../components/AlmeApp/AlmeAppState';

import { AlmeResponseNext } from '@alme/types/response/AlmeResponse';
import { ConfigurationResponse } from '@alme/types/response/ConfigurationResponse';
import { UnitDisplayLink } from '@alme/types/response/UnitDisplayLink';

import { NotificationResponseNext } from '@alme/types/response/NotificationResponse';
import { LiveChatNotificationResponseNext } from '@alme/types/response/LiveChatNotificationResponse';

import { AlmeApiNext } from '@alme/api-ts/Alme/AlmeApi';
import { ConverseRequestDetailsNext } from '@alme/api-ts/Alme/ConverseRequestDetails';
import { RequestUnitRequestDetailsNext } from '@alme/api-ts/Alme/RequestUnitRequestDetails';
import { HistoryRequestDetailsNext } from '@alme/api-ts/Alme/HistoryRequestDetails';
import { AppEventRequestDetailsNext } from '@alme/api-ts/Alme/AppEventRequestDetails';
import { ConfigurationRequestDetailsNext } from '@alme/api-ts/Alme/ConfigurationRequestDetails';
import { ReportingEventRequestDetailsNext } from '@alme/api-ts/Alme/ReportingEventRequestDetails';
import { EntryOriginTypesNext } from '@alme/api-ts/Alme/EntryOriginTypes';

import { LiveChatNotificationApiNext } from '@alme/api-ts/LiveChat/LiveChatNotificationApi';
import { LiveChatNotificationRequestDetailsNext } from '@alme/api-ts/LiveChat/LiveChatNotificationRequestDetails';
import { deviceDetect } from 'react-device-detect';

import deepmerge from 'deepmerge';

import { AlmeApiUtilsConfig } from './AlmeApiUtilsConfig';
import { AlmeSessionData } from '@alme/session-ts/AlmeSessionData';

import defaultLogger from '../Logger';
import { AlmeSessionUtils } from "./AlmeSessionUtils";
import { ReportingEventResponseNext } from '@alme/types/response/ReportingEventResponse';
import { NotificationRequestDetailsNext } from '@alme/api-ts/Alme/NotificationRequestDetails';
import { EndConversationRequestDetailsNext } from '@alme/api-ts/Alme/EndConversationRequestDetails';
import { createIntl, createIntlCache } from 'react-intl';
import { getCurrentIntlConfig } from '../utils/PersonaUtils';

interface FpmlUrlProperties {
    AbsolutePath: string;
    Protocol: string;
    Host: string;
    HostName: string;
    Port: string;
    Uri: string;
    Query: string;
    Fragment: string;
    Origin: string;
    // Not implementing PageName until needed
    // (it complicates things a bit)
}

export class AlmeApiUtils {
    private almeState: AlmeAppState;
    private setAlmeState: (state: AlmeAppState) => void;
    private almeConfig: AlmeApiUtilsConfig;
    private almeSession: AlmeSessionData;
    private almeSessionUtils: AlmeSessionUtils | undefined;
    private almeApi: AlmeApiNext;
    private liveChatNotificationApi: LiveChatNotificationApiNext;
    private validateAlmeSessionData: (response: AlmeResponseNext | AlmeResponseNext[]) => void;
    private verintAccessKey: string | undefined;
    private currentLanguage: string;

    constructor(almeState: AlmeAppState, setAlmeState: (state: AlmeAppState) => void, almeSession: AlmeSessionData, almeSessionUtils: AlmeSessionUtils | undefined, validateAlmeSessionData: (response: AlmeResponseNext | AlmeResponseNext[]) => void, almeConfig: AlmeApiUtilsConfig, almeApi: AlmeApiNext, liveChatNotificationApi: LiveChatNotificationApiNext, verintAccessKey?: string) {
        this.almeState = almeState;
        this.setAlmeState = setAlmeState;
        this.almeSession = almeSession;
        this.almeSessionUtils = almeSessionUtils;
        this.validateAlmeSessionData = validateAlmeSessionData;
        this.almeConfig = almeConfig;
        this.almeApi = almeApi;
        this.liveChatNotificationApi = liveChatNotificationApi;
        this.verintAccessKey = verintAccessKey;
        this.currentLanguage = getCurrentIntlConfig().locale;
    }

    private getErrorResponse(): AlmeResponseNext {
        const currentIntlConfig = getCurrentIntlConfig();
        const cache = createIntlCache();
        const intl = createIntl({ locale: currentIntlConfig.locale, messages: currentIntlConfig.messages }, cache);

        const responseRevision = this.almeState.history.length ?
            (this.almeState.history[this.almeState.history.length - 1].responseRevision + 1) :
            1;

        return {
            id: this.almeConfig.apiUtils.errorResponse.entryId,
            userId: this.almeSession.userId,
            text: intl.formatMessage({
                id: "apiUtils.systemErrorMessage",
                defaultMessage: 'Default system error message',
                description: 'AlmeApiUtils system error message',
            }),
            responseRevision: responseRevision,
            displayLinkCollection: {
                Sections: [],
                Metadata: null,
            },
            navUrl: null,
            responseActions: [],
            deferredAppCall: null,
            maskedInput: null,
            isReverted: false,
            extensionData: {
                entryId: this.almeConfig.apiUtils.errorResponse.entryId,
            },
        };
    }

    private createCustomParams(): Record<string, unknown> {
        // TODO: add your custom params here.
        return {};
    }

    /**
     * Create Context parameter object.
     * @param location DOM window location object
     */
    private createContextObject(location: Location): { Context: { CurrentUrl: FpmlUrlProperties; CurrentDevice: Record<string, unknown> } } {
        return {
            Context: {
                CurrentUrl: this.createPageAwarenessObject(location),
                CurrentDevice: this.createDeviceInformationObject(),
            },
        };
    }

    /**
     * Create Page Awareness parameter object.
     * @param location DOM window location object
     */
    private createPageAwarenessObject(location: Location): FpmlUrlProperties {
        return {
            AbsolutePath: location.href,
            Protocol: location.protocol,
            Host: location.host,
            HostName: location.hostname,
            Port: location.port,
            Uri: location.pathname,
            Query: location.search,
            Fragment: location.hash,
            Origin: location.origin,
        };
    }

    /**
     * Create Device Information parameter object.
     */
    private createDeviceInformationObject(): Record<string, unknown> {
        return deviceDetect() as Record<string, unknown>;
    }

    /**
     * Create parameter object and
     * deep merge with existing parameters, custom parameters, and page awareness parameters.
     * @see deepmerge.all
     * @param parameters Current input parameters
     * @returns Input parameters merged with page awareness parameters
     */
    private addRequestParams(parameters: Record<string, unknown>): Record<string, unknown> {
        const pageAwarenessParams = this.createContextObject(window.location);
        const customParams = this.createCustomParams();
        const uiVersionParam = {
            UiVersion: this.almeConfig.apiUtils.uiVersion,
        };
        return deepmerge.all([uiVersionParam, pageAwarenessParams, customParams, parameters]) as Record<string, unknown>;
    }

    private sendErrorResponse(): void {
        const errorResponse = this.getErrorResponse();
        this.appendToHistory(errorResponse);
        this.validateAlmeSessionData(errorResponse);
    }

    /**
     * Submits the users input to alme using the Typescript Alme API.
     * @see AlmeApiNext.SendInputAsync
     * @see ConverseRequestDetailsNext
     * @see [Alme Api Converse Request](https://confluence.nextit.com/pages/viewpage.action?pageId=35688489)
     * @see [Understanding Entry Types and Origins](https://confluence.nextit.com/display/AlmeTech/Understanding+Entry+Types+and+Origins)
     */
    public async sendAlmeInput(input: string, origin: EntryOriginTypesNext = 'Typed', displayText: string = input, parameters: Record<string, unknown> = {}): Promise<AlmeResponseNext> {
        const requestParams = this.addRequestParams(parameters);
        const request = new ConverseRequestDetailsNext(this.almeSession.userId, this.almeSession.sessionId, input, origin, displayText, requestParams,
            this.almeState.agentChannel, this.currentLanguage, undefined, this.verintAccessKey);
        const response = await this.almeApi.SendInputAsync(request);
        this.almeSessionUtils?.refreshSessionTimer();
        this.validateAlmeSessionData(response);
        return response;
    }

    /**
     * Submits the users input to alme using the Typescript Alme API and appends to history.
     * @see AlmeApiNext.SendInputAsync
     * @see ConverseRequestDetailsNext
     * @see [Alme Api Converse Request](https://confluence.nextit.com/pages/viewpage.action?pageId=35688489)
     * @see [Understanding Entry Types and Origins](https://confluence.nextit.com/display/AlmeTech/Understanding+Entry+Types+and+Origins)
     */
    public async submitAlmeInput(input: string, origin: EntryOriginTypesNext = 'Typed', displayText: string = input, parameters: Record<string, unknown> = {}): Promise<void> {
        try {
            const response = await this.sendAlmeInput(input, origin, displayText, parameters);
            this.appendToHistory(response);
            this.validateAlmeSessionData(response);
        } catch (error) {
            defaultLogger.error(error);
            this.sendErrorResponse();
        }
    }

    /**
     * Submits a Request Unit request
     * @see UnitDisplayLink
     */
    public async submitUnitRequest(unitLink: UnitDisplayLink, parameters: Record<string, unknown> = {}): Promise<void> {
        const apiParams = this.addRequestParams(parameters);
        const unitRequest = new RequestUnitRequestDetailsNext(this.almeSession.userId, this.almeSession.sessionId, unitLink.Metadata.UnitUID,
            unitLink.DisplayText, 'Click', apiParams, this.currentLanguage, this.almeState.agentChannel, undefined, this.verintAccessKey);
        try {
            const response = await this.almeApi.SendRequestUnitAsync(unitRequest);
            this.almeSessionUtils?.refreshSessionTimer();
            this.appendToHistory(response);
            this.validateAlmeSessionData(response);
        } catch (error) {
            defaultLogger.error(error);
            this.sendErrorResponse();
        }
    }

    /**
     * Sends an Alme GetHistory request and returns the history response or empty AlmeResponseNext[] if no history exists.
     * @see AlmeApiNext.GetHistoryAsync
     * @see HistoryRequestDetailsNext
     * @see [api/ConversationSupport/GetHistory](https://confluence.nextit.com/pages/viewpage.action?pageId=35688491)
     */
    public async loadAlmeHistory(): Promise<AlmeResponseNext[]> {
        const historyRequest = new HistoryRequestDetailsNext(this.almeSession.sessionId);
        try {
            const responses = await this.almeApi.GetHistoryAsync(historyRequest);
            this.almeSessionUtils?.startSessionTimer();
            this.validateAlmeSessionData(responses);
            return responses;
        } catch (error) {
            defaultLogger.error(error);
            return [];
        }
    }

    /**
     * Sends an Alme Application Event request and appends to the history.
     * @see [api/Conversation/appEvent](https://confluence.nextit.com/pages/viewpage.action?pageId=35688488)
     * @see [Understanding Entry Types and Origins](https://confluence.nextit.com/display/AlmeTech/Understanding+Entry+Types+and+Origins)
     */
    public async submitAppEvent(eventName: string, origin: EntryOriginTypesNext = 'Internal', parameters: Record<string, unknown> = {}): Promise<void> {
        try {
            const response = await this.sendAppEvent(eventName, origin, parameters);
            this.appendToHistory(response);
            this.validateAlmeSessionData(response);
        } catch (error) {
            defaultLogger.error(error);
            this.sendErrorResponse();
        }
    }

    /**
     * Sends an Alme Application Event request and returns the resulting AlmeResponseNext.  This does not automatically append to the history.
     * @see AlmeResponseNext
     * @see [api/Conversation/appEvent](https://confluence.nextit.com/pages/viewpage.action?pageId=35688488)
     * @see [Understanding Entry Types and Origins](https://confluence.nextit.com/display/AlmeTech/Understanding+Entry+Types+and+Origins)
     */
    public async sendAppEvent(eventName: string, origin: EntryOriginTypesNext = 'Internal', parameters: Record<string, unknown> = {}): Promise<AlmeResponseNext> {
        const apiParams = this.addRequestParams(parameters);
        const appEventRequest = new AppEventRequestDetailsNext(this.almeSession.userId, this.almeSession.sessionId, eventName, origin, apiParams,
            this.almeState.agentChannel, this.currentLanguage, undefined, this.verintAccessKey);
        try {
            const response = await this.almeApi.SendAppEventAsync(appEventRequest);
            this.almeSessionUtils?.refreshSessionTimer();
            this.validateAlmeSessionData(response);
            return response;
        } catch (error) {
            defaultLogger.error(error);
            return this.getErrorResponse();
        }
    }

    /**
     * Sends an Alme GetConfiguration request.
     * @see ConfigurationRequestDetailsNext
     * @see [api/Configuration/GetConfiguration](https://confluence.nextit.com/pages/viewpage.action?pageId=35688487)
     */
    public async getAlmeConfiguration(): Promise<ConfigurationResponse | null> {
        const configRequest = new ConfigurationRequestDetailsNext(this.almeState.agentChannel);
        try {
            return await this.almeApi.GetConfigurationAsync(configRequest);
        } catch (error) {
            defaultLogger.error(error);
            return null;
        }
    }

    /**
     * Submits an Alme ReportingEvent and returns the success/failure as a boolean.
     * @see ReportingEventRequestDetailsNext
     * @param variableName reporting event variable name
     * @param variableValue reporting event variable value
     */
    public async submitReportingEvent(variableName: string, variableValue?: string | Record<string, unknown>): Promise<ReportingEventResponseNext | null> {
        const request = new ReportingEventRequestDetailsNext(this.almeSession.userId, this.almeSession.sessionId, variableName, variableValue || '',
            this.almeState.agentChannel, undefined, this.verintAccessKey);
        try {
            return await this.almeApi.SendReportingEventAsync(request);
        } catch (error) {
            defaultLogger.error(error);
            return null;
        }
    }

    public async endConversation(): Promise<boolean> {
        try {
            const request = new EndConversationRequestDetailsNext(this.almeSession.sessionId);
            return await this.almeApi.EndConversationAsync(request);
        } catch (error) {
            defaultLogger.error(error);
            return false;
        }
    }

    public async getNotifications(): Promise<void> {
        try {
            const request = new NotificationRequestDetailsNext(this.almeSession.sessionId);
            const response = await this.almeApi.GetPendingMessagesAsync(request);
            this.almeSessionUtils?.refreshSessionTimer();
            if (response) {
                this.appendNotificationToHistory(response);
            }
        } catch (error) {
            defaultLogger.error(error);
        }
    }

    public async getLiveChatNotifications(): Promise<void> {
        try {
            const request = new LiveChatNotificationRequestDetailsNext(this.almeSession.sessionId, this.almeState.liveChatIndex + 1);
            const response = await this.liveChatNotificationApi.GetLiveChatNotificationAsync(request);
            this.almeSessionUtils?.refreshSessionTimer();
            if (response) {
                this.appendLiveChatToHistory(response);
            }
        } catch (error) {
            defaultLogger.error(error);
        }
    }

    private appendToHistory(response: AlmeResponseNext): void {
        if (!response || !response.userId) {
            return;
        }

        const isLiveChatConnected = response?.extensionData?.MessageSource === 'LiveChat';

        const history = this.almeState.history;
        history.push(response);
        this.setAlmeState({
            ...this.almeState,
            history,
            isLiveChatConnected,
        });
    }

    private appendNotificationToHistory(response: NotificationResponseNext[]) {
        if (!response || !response.length) {
            return;
        }

        const history = this.almeState.history;
        history.push(...response);

        this.setAlmeState({
            ...this.almeState,
            history,
        });
    }

    private appendLiveChatToHistory(response: LiveChatNotificationResponseNext[]) {
        if (!response || !response.length) {
            return;
        }

        const history = this.almeState.history;
        history.push(...response);

        let liveChatIndex = this.almeState.liveChatIndex;
        const index = response[response.length - 1].extensionData.MessageIndex;
        if (typeof index === 'number' && !isNaN(index) && index >= 0) {
            // Note: it is possible for index to be 0.  This typically starts at 1.
            liveChatIndex = index;
        }

        this.setAlmeState({
            ...this.almeState,
            history,
            liveChatIndex,
            isLiveChatConnected: true,
        });
    }
}
